import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAdmin from '../../hooks/useAdmin';
import axiosInstance from '../../services/axiosInstance';
import Navbar from '../../components/Navbar';

function AdaptiveHome() {
  const { isAdmin, isLoading } = useAdmin();
  const [userEstablishments, setUserEstablishments] = useState([]);
  const [allEstablishments, setAllEstablishments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCreateEstablishmentModal, setShowCreateEstablishmentModal] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    admin: false
  });
  const [newEstablishment, setNewEstablishment] = useState({
    name: '',
    address: '',
    city: '',
    insee: '',
    lat: '',
    lon: '',
    email: '',
    phone: '',
    description: '',
    horaire: {
      lundi: { ouvert: false, heures: '' },
      mardi: { ouvert: false, heures: '' },
      mercredi: { ouvert: false, heures: '' },
      jeudi: { ouvert: false, heures: '' },
      vendredi: { ouvert: false, heures: '' },
      samedi: { ouvert: false, heures: '' },
      dimanche: { ouvert: false, heures: '' }
    }
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        
        if (isAdmin) {
          // Si admin, récupérer tous les établissements avec leurs utilisateurs
          const establishmentsResponse = await axiosInstance.get('/admin/establishments');
          const usersResponse = await axiosInstance.get('/admin/partner_users');
          
          // Stocker tous les utilisateurs pour la sélection
          setAllUsers(usersResponse.data.users);
          
          // Enrichir les établissements avec les informations des utilisateurs
          const establishmentsWithUsers = establishmentsResponse.data.establishments.map(establishment => {
            const establishmentUsers = usersResponse.data.users.filter(user => 
              user.establishments && user.establishments.some(est => est.id === establishment.id)
            );
            return {
              ...establishment,
              users: establishmentUsers
            };
          });
          
          setAllEstablishments(establishmentsWithUsers);
        } else {
          // Si utilisateur normal, récupérer ses établissements
          const establishmentsResponse = await axiosInstance.get(`/partner_users/${userId}/get_establishments`);
          setUserEstablishments(establishmentsResponse.data.establishment || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoadingData(false);
      }
    };

    if (!isLoading) {
      fetchUserData();
    }
  }, [isAdmin, isLoading]);

  const handleCreateUser = async () => {
    try {
      let userId;
      
      if (isCreatingNewUser) {
        // Créer un nouvel utilisateur
        if (!newUser.username || !newUser.email || !newUser.password) {
          alert('Veuillez remplir tous les champs obligatoires');
          return;
        }

        const userResponse = await axiosInstance.post('/admin/partner_users', newUser);
        userId = userResponse.data.user.id;
      } else {
        // Utiliser un utilisateur existant
        if (!selectedUserId) {
          alert('Veuillez sélectionner un utilisateur');
          return;
        }
        userId = selectedUserId;
      }
      
      // Assigner l'utilisateur à l'établissement
      await axiosInstance.post(`/admin/establishments/${selectedEstablishment.id}/assign_user`, {
        user_id: userId
      });

      // Rafraîchir les données
      const establishmentsResponse = await axiosInstance.get('/admin/establishments');
      const usersResponse = await axiosInstance.get('/admin/partner_users');
      
      const establishmentsWithUsers = establishmentsResponse.data.establishments.map(establishment => {
        const establishmentUsers = usersResponse.data.users.filter(user => 
          user.establishments && user.establishments.some(est => est.id === establishment.id)
        );
        return {
          ...establishment,
          users: establishmentUsers
        };
      });
      
      setAllEstablishments(establishmentsWithUsers);
      setAllUsers(usersResponse.data.users); // Mettre à jour la liste des utilisateurs
      
      // Reset des états
      setNewUser({ username: '', email: '', password: '', admin: false });
      setSelectedUserId('');
      setIsCreatingNewUser(true);
      setShowCreateUserModal(false);
      setSelectedEstablishment(null);
      
      const action = isCreatingNewUser ? 'créé et assigné' : 'assigné';
      alert(`Utilisateur ${action} avec succès !`);
    } catch (error) {
      console.error('Erreur lors de l\'opération:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleCreateEstablishment = async () => {
    try {
      if (!newEstablishment.name || !newEstablishment.address) {
        alert('Veuillez remplir au minimum le nom et l\'adresse');
        return;
      }

      const userId = localStorage.getItem('user_id');
      
      const establishmentData = {
        ...newEstablishment,
        partner_user_id: parseInt(userId)
      };

      const response = await axiosInstance.post('/partner_users/add_establishment', establishmentData);
      
      // Rafraîchir les données selon le type d'utilisateur
      if (isAdmin) {
        const establishmentsResponse = await axiosInstance.get('/admin/establishments');
        const usersResponse = await axiosInstance.get('/admin/partner_users');
        
        const establishmentsWithUsers = establishmentsResponse.data.establishments.map(establishment => {
          const establishmentUsers = usersResponse.data.users.filter(user => 
            user.establishments && user.establishments.some(est => est.id === establishment.id)
          );
          return {
            ...establishment,
            users: establishmentUsers
          };
        });
        
        setAllEstablishments(establishmentsWithUsers);
      } else {
        const establishmentsResponse = await axiosInstance.get(`/partner_users/${userId}/get_establishments`);
        setUserEstablishments(establishmentsResponse.data.establishment || []);
      }

      // Reset du formulaire
      setNewEstablishment({
        name: '',
        address: '',
        city: '',
        insee: '',
        lat: '',
        lon: '',
        email: '',
        phone: '',
        description: '',
        horaire: {
          lundi: { ouvert: false, heures: '' },
          mardi: { ouvert: false, heures: '' },
          mercredi: { ouvert: false, heures: '' },
          jeudi: { ouvert: false, heures: '' },
          vendredi: { ouvert: false, heures: '' },
          samedi: { ouvert: false, heures: '' },
          dimanche: { ouvert: false, heures: '' }
        }
      });
      
      setShowCreateEstablishmentModal(false);
      alert('Établissement créé avec succès !');
      
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert(error.response?.data?.error || 'Erreur lors de la création de l\'établissement');
    }
  };

  const handleRemoveUserFromEstablishment = async (userId, establishmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cet utilisateur de cet établissement ?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/establishments/${establishmentId}/unassign_user/${userId}`);
      
      // Rafraîchir les données
      const establishmentsResponse = await axiosInstance.get('/admin/establishments');
      const usersResponse = await axiosInstance.get('/admin/partner_users');
      
      const establishmentsWithUsers = establishmentsResponse.data.establishments.map(establishment => {
        const establishmentUsers = usersResponse.data.users.filter(user => 
          user.establishments && user.establishments.some(est => est.id === establishment.id)
        );
        return {
          ...establishment,
          users: establishmentUsers
        };
      });
      
      setAllEstablishments(establishmentsWithUsers);
      alert('Utilisateur retiré avec succès !');
    } catch (error) {
      console.error('Erreur lors du retrait:', error);
      alert(error.response?.data?.error || 'Erreur lors du retrait');
    }
  };

  const styles = {
    container: {
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    header: {
      backgroundColor: '#ffffff',
      padding: '48px 32px',
      borderBottom: '1px solid #e2e8f0',
      marginBottom: '32px'
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      textAlign: 'center'
    },
    welcomeTitle: {
      fontSize: '40px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 16px 0',
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    subtitle: {
      fontSize: '18px',
      color: '#64748b',
      margin: 0,
      maxWidth: '600px',
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: '1.6'
    },
    content: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 32px'
    },
    // Nouveau style pour la barre d'actions administrateur
    adminActionsBar: {
      display: 'flex',
      gap: '16px',
      marginBottom: '32px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    adminActionButton: {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 32px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
      transition: 'all 0.2s ease-in-out',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    createButtonContainer: {
      marginBottom: '32px',
      textAlign: 'center'
    },
    createButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 32px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
      transition: 'all 0.2s ease-in-out'
    },
    establishmentsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
      gap: '32px',
      marginBottom: '48px'
    },
    establishmentCard: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease-in-out'
    },
    establishmentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px'
    },
    establishmentInfo: {
      flex: 1
    },
    establishmentName: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px'
    },
    establishmentDetails: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '4px'
    },
    establishmentId: {
      fontSize: '12px',
      color: '#9ca3af',
      fontWeight: '500'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      marginTop: '12px'
    },
    visibleBadge: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    hiddenBadge: {
      backgroundColor: '#fef2f2',
      color: '#dc2626'
    },
    usersSection: {
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '1px solid #f1f5f9'
    },
    usersSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },
    usersTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0
    },
    addUserButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    usersList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    userItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    userInfo: {
      flex: 1
    },
    userName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1e293b',
      marginBottom: '2px'
    },
    userEmail: {
      fontSize: '12px',
      color: '#64748b'
    },
    userBadge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '500',
      marginLeft: '8px'
    },
    adminBadge: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    normalUserBadge: {
      backgroundColor: '#dbeafe',
      color: '#2563eb'
    },
    removeUserButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '4px',
      padding: '4px 8px',
      fontSize: '10px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    noUsers: {
      textAlign: 'center',
      color: '#9ca3af',
      fontStyle: 'italic',
      fontSize: '14px',
      padding: '16px'
    },
    manageButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.2s ease-in-out',
      display: 'inline-block',
      marginTop: '16px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '24px',
      textAlign: 'center'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box',
      height: '100px',
      resize: 'vertical'
    },
    checkbox: {
      width: '16px',
      height: '16px',
      marginRight: '8px'
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      marginTop: '32px'
    },
    button: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      flex: 1
    },
    secondaryButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      flex: 1
    },
    emptyState: {
      textAlign: 'center',
      padding: '80px 20px',
      color: '#64748b'
    },
    emptyStateIcon: {
      fontSize: '64px',
      marginBottom: '24px'
    },
    emptyStateTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    emptyStateText: {
      fontSize: '16px',
      color: '#6b7280'
    },
    modeSelector: {
      marginBottom: '24px'
    },
    modeSelectorButtons: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px'
    },
    modeButton: {
      flex: 1,
      padding: '10px 16px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    noAvailableUsers: {
      fontSize: '14px',
      color: '#64748b',
      marginTop: '8px',
      fontStyle: 'italic'
    }
  };

  if (isLoading || loadingData) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={{ ...styles.content, textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Navbar />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.welcomeTitle}>
            {isAdmin ? 'Réseau d\'Établissements #CSIBON' : 'Bienvenue sur votre espace'}
          </h1>
          <p style={styles.subtitle}>
            {isAdmin 
              ? 'Gérez vos établissements et les utilisateurs qui les administrent'
              : 'Gérez vos établissements et suivez votre activité'
            }
          </p>
        </div>
      </div>

      <div style={styles.content}>
        {/* Barre d'actions administrateur */}
        {isAdmin && (
          <div style={styles.adminActionsBar}>
            <Link
              to="/admin/users"
              style={styles.adminActionButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3b82f6';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              👥 Gérer les Utilisateurs Partenaires
            </Link>
          </div>
        )}

        {/* Bouton pour créer un nouvel établissement */}
        <div style={styles.createButtonContainer}>
          <button
            style={styles.createButton}
            onClick={() => setShowCreateEstablishmentModal(true)}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#047857';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#059669';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            + Créer un nouvel établissement
          </button>
        </div>

        {isAdmin ? (
          // Vue Administrateur - Tous les établissements avec leurs utilisateurs
          <>
            {allEstablishments.length > 0 ? (
              <div style={styles.establishmentsGrid}>
                {allEstablishments.map((establishment) => (
                  <div
                    key={establishment.id}
                    style={styles.establishmentCard}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px 0 rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div style={styles.establishmentHeader}>
                      <div style={styles.establishmentInfo}>
                        <div style={styles.establishmentName}>{establishment.name}</div>
                        <div style={styles.establishmentDetails}>📍 {establishment.city || 'Ville non renseignée'}</div>
                        <div style={styles.establishmentDetails}>📧 {establishment.email || 'Email non renseigné'}</div>
                        <div style={styles.establishmentDetails}>📞 {establishment.phone || 'Téléphone non renseigné'}</div>
                        <div style={styles.establishmentId}>ID: {establishment.id}</div>
                        <span style={{
                          ...styles.statusBadge,
                          ...(establishment.visible ? styles.visibleBadge : styles.hiddenBadge)
                        }}>
                          {establishment.visible ? '✅ Visible' : '❌ Masqué'}
                        </span>
                      </div>
                    </div>

                    {/* Section des utilisateurs */}
                    <div style={styles.usersSection}>
                      <div style={styles.usersSectionHeader}>
                        <h4 style={styles.usersTitle}>
                          Gestionnaires ({establishment.users ? establishment.users.length : 0})
                        </h4>
                        <button
                          style={styles.addUserButton}
                          onClick={() => {
                            setSelectedEstablishment(establishment);
                            setShowCreateUserModal(true);
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                        >
                          + Ajouter
                        </button>
                      </div>

                      <div style={styles.usersList}>
                        {establishment.users && establishment.users.length > 0 ? (
                          establishment.users.map((user) => (
                            <div key={user.id} style={styles.userItem}>
                              <div style={styles.userInfo}>
                                <div style={styles.userName}>
                                  {user.username}
                                  <span style={{
                                    ...styles.userBadge,
                                    ...(user.admin ? styles.adminBadge : styles.normalUserBadge)
                                  }}>
                                    {user.admin ? 'Admin' : 'User'}
                                  </span>
                                </div>
                                <div style={styles.userEmail}>{user.email}</div>
                              </div>
                              {!user.admin && (
                                <button
                                  style={styles.removeUserButton}
                                  onClick={() => handleRemoveUserFromEstablishment(user.id, establishment.id)}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                                >
                                  Retirer
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={styles.noUsers}>
                            Aucun gestionnaire assigné
                          </div>
                        )}
                      </div>
                    </div>

                    <Link
                      to={`/admin/${establishment.id}`}
                      style={styles.manageButton}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e2e8f0';
                        e.target.style.color = '#334155';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f1f5f9';
                        e.target.style.color = '#475569';
                      }}
                    >
                      🔧 Administrer l'établissement →
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyStateIcon}>🏪</div>
                <h3 style={styles.emptyStateTitle}>
                  Aucun établissement trouvé
                </h3>
                <p style={styles.emptyStateText}>
                  Aucun établissement n'est encore enregistré dans votre réseau.
                </p>
              </div>
            )}
          </>
        ) : (
          // Vue Utilisateur Normal - Ses établissements uniquement
          <>
            {userEstablishments.length > 0 ? (
              <div style={styles.establishmentsGrid}>
                {userEstablishments.map((establishment) => (
                  <div
                    key={establishment.id}
                    style={styles.establishmentCard}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px 0 rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div style={styles.establishmentHeader}>
                      <div style={styles.establishmentInfo}>
                        <div style={styles.establishmentName}>{establishment.name}</div>
                        <div style={styles.establishmentDetails}>📍 {establishment.city || 'Ville non renseignée'}</div>
                        <div style={styles.establishmentId}>ID: {establishment.id}</div>
                      </div>
                    </div>

                    <Link
                      to={`/admin/${establishment.id}`}
                      style={styles.manageButton}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e2e8f0';
                        e.target.style.color = '#334155';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f1f5f9';
                        e.target.style.color = '#475569';
                      }}
                    >
                      🔧 Gérer l'établissement →
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyStateIcon}>🏪</div>
                <h3 style={styles.emptyStateTitle}>
                  Aucun établissement assigné
                </h3>
                <p style={styles.emptyStateText}>
                  Vous n'êtes associé à aucun établissement pour le moment.
                </p>
              </div>
            )}
          </>
        )}
      </div>
 
      {/* Modal de création/assignation d'utilisateur */}
      {showCreateUserModal && selectedEstablishment && (
        <div style={styles.modal} onClick={() => setShowCreateUserModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              Assigner un gestionnaire à {selectedEstablishment.name}
            </h2>
            
            {/* Sélecteur de mode */}
            <div style={styles.modeSelector}>
              <div style={styles.modeSelectorButtons}>
                <button
                  style={{
                    ...styles.modeButton,
                    backgroundColor: isCreatingNewUser ? '#059669' : '#f1f5f9',
                    color: isCreatingNewUser ? '#ffffff' : '#475569'
                  }}
                  onClick={() => setIsCreatingNewUser(true)}
                >
                  Créer un nouvel utilisateur
               </button>
               <button
                 style={{
                   ...styles.modeButton,
                   backgroundColor: !isCreatingNewUser ? '#059669' : '#f1f5f9',
                   color: !isCreatingNewUser ? '#ffffff' : '#475569'
                 }}
                 onClick={() => setIsCreatingNewUser(false)}
               >
                 Sélectionner un utilisateur existant
               </button>
             </div>
           </div>
 
           {isCreatingNewUser ? (
             // Formulaire de création d'utilisateur
             <>
               <div style={styles.inputGroup}>
                 <label style={styles.label}>Nom d'utilisateur *</label>
                 <input
                   type="text"
                   value={newUser.username}
                   onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                   style={styles.input}
                   placeholder="Nom d'utilisateur"
                 />
               </div>
 
               <div style={styles.inputGroup}>
                 <label style={styles.label}>Email *</label>
                 <input
                   type="email"
                   value={newUser.email}
                   onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                   style={styles.input}
                   placeholder="email@exemple.com"
                 />
               </div>
 
               <div style={styles.inputGroup}>
                 <label style={styles.label}>Mot de passe *</label>
                 <input
                   type="password"
                   value={newUser.password}
                   onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                   style={styles.input}
                   placeholder="Mot de passe"
                 />
               </div>
 
               <div style={styles.checkboxGroup}>
                 <input
                   type="checkbox"
                   checked={newUser.admin}
                   onChange={(e) => setNewUser({...newUser, admin: e.target.checked})}
                   style={styles.checkbox}
                 />
                 <label style={styles.label}>Administrateur</label>
               </div>
             </>
           ) : (
             // Sélecteur d'utilisateur existant
             <div style={styles.inputGroup}>
               <label style={styles.label}>Sélectionner un utilisateur *</label>
               <select
                 value={selectedUserId}
                 onChange={(e) => setSelectedUserId(e.target.value)}
                 style={styles.input}
               >
                 <option value="">-- Choisir un utilisateur --</option>
                 {allUsers
                   .filter(user => 
                     // Filtrer les utilisateurs qui ne sont pas déjà assignés à cet établissement
                     !selectedEstablishment.users?.some(assignedUser => assignedUser.id === user.id)
                   )
                   .map(user => (
                     <option key={user.id} value={user.id}>
                       {user.username} ({user.email}) {user.admin ? '- Admin' : ''}
                     </option>
                   ))
                 }
               </select>
               {allUsers.filter(user => 
                 !selectedEstablishment.users?.some(assignedUser => assignedUser.id === user.id)
               ).length === 0 && (
                 <div style={styles.noAvailableUsers}>
                   Tous les utilisateurs sont déjà assignés à cet établissement
                 </div>
               )}
             </div>
           )}
 
           <div style={styles.buttonGroup}>
             <button
               style={styles.secondaryButton}
               onClick={() => {
                 setShowCreateUserModal(false);
                 setNewUser({ username: '', email: '', password: '', admin: false });
                 setSelectedUserId('');
                 setIsCreatingNewUser(true);
                 setSelectedEstablishment(null);
               }}
             >
               Annuler
             </button>
             <button
               style={styles.button}
               onClick={handleCreateUser}
             >
               {isCreatingNewUser ? 'Créer et assigner' : 'Assigner l\'utilisateur'}
             </button>
           </div>
         </div>
       </div>
     )}
 
     {/* Modal de création d'établissement */}
     {showCreateEstablishmentModal && (
       <div style={styles.modal} onClick={() => setShowCreateEstablishmentModal(false)}>
       <div style={{...styles.modalContent, maxWidth: '600px'}} onClick={(e) => e.stopPropagation()}>
         <h2 style={styles.modalTitle}>Créer un nouvel établissement</h2>
         
         <div style={styles.inputGroup}>
           <label style={styles.label}>Nom de l'établissement *</label>
           <input
             type="text"
             value={newEstablishment.name}
             onChange={(e) => setNewEstablishment({...newEstablishment, name: e.target.value})}
             style={styles.input}
             placeholder="Nom de l'établissement"
           />
         </div>
 
         <div style={styles.inputGroup}>
           <label style={styles.label}>Adresse *</label>
           <input
             type="text"
             value={newEstablishment.address}
             onChange={(e) => setNewEstablishment({...newEstablishment, address: e.target.value})}
             style={styles.input}
             placeholder="Adresse complète"
           />
         </div>
 
         <div style={styles.inputGroup}>
           <label style={styles.label}>Ville</label>
           <input
             type="text"
             value={newEstablishment.city}
             onChange={(e) => setNewEstablishment({...newEstablishment, city: e.target.value})}
             style={styles.input}
             placeholder="Ville"
           />
         </div>
 
         <div style={styles.inputGroup}>
           <label style={styles.label}>Code INSEE</label>
           <input
             type="text"
             value={newEstablishment.insee}
             onChange={(e) => setNewEstablishment({...newEstablishment, insee: e.target.value})}
             style={styles.input}
             placeholder="Code INSEE"
           />
         </div>
 
         <div style={{display: 'flex', gap: '12px'}}>
           <div style={{...styles.inputGroup, flex: 1}}>
             <label style={styles.label}>Latitude</label>
             <input
               type="text"
               value={newEstablishment.lat}
               onChange={(e) => setNewEstablishment({...newEstablishment, lat: e.target.value})}
               style={styles.input}
               placeholder="Latitude"
             />
           </div>
           <div style={{...styles.inputGroup, flex: 1}}>
             <label style={styles.label}>Longitude</label>
             <input
               type="text"
               value={newEstablishment.lon}
               onChange={(e) => setNewEstablishment({...newEstablishment, lon: e.target.value})}
               style={styles.input}
               placeholder="Longitude"
             />
           </div>
         </div>
 
         <div style={styles.inputGroup}>
           <label style={styles.label}>Email</label>
           <input
             type="email"
             value={newEstablishment.email}
             onChange={(e) => setNewEstablishment({...newEstablishment, email: e.target.value})}
             style={styles.input}
             placeholder="email@exemple.com"
           />
         </div>
 
         <div style={styles.inputGroup}>
           <label style={styles.label}>Téléphone</label>
           <input
             type="tel"
             value={newEstablishment.phone}
             onChange={(e) => setNewEstablishment({...newEstablishment, phone: e.target.value})}
             style={styles.input}
             placeholder="Numéro de téléphone"
           />
         </div>
 
         <div style={styles.inputGroup}>
           <label style={styles.label}>Description</label>
           <textarea
             value={newEstablishment.description}
             onChange={(e) => setNewEstablishment({...newEstablishment, description: e.target.value})}
             style={styles.textarea}
             placeholder="Description de l'établissement"
           />
         </div>
 
         <div style={styles.buttonGroup}>
           <button
             style={styles.secondaryButton}
             onClick={() => {
               setShowCreateEstablishmentModal(false);
               setNewEstablishment({
                 name: '',
                 address: '',
                 city: '',
                 insee: '',
                 lat: '',
                 lon: '',
                 email: '',
                 phone: '',
                 description: '',
                 horaire: {
                   lundi: { ouvert: false, heures: '' },
                   mardi: { ouvert: false, heures: '' },
                   mercredi: { ouvert: false, heures: '' },
                   jeudi: { ouvert: false, heures: '' },
                   vendredi: { ouvert: false, heures: '' },
                   samedi: { ouvert: false, heures: '' },
                   dimanche: { ouvert: false, heures: '' }
                 }
               });
             }}
           >
             Annuler
           </button>
           <button
             style={styles.button}
             onClick={handleCreateEstablishment}
           >
             Créer l'établissement
           </button>
         </div>
       </div>
     </div>
   )}
 </div>
 );
 }
 
 export default AdaptiveHome;